import { Controller, useWatch, type Control, type UseFormSetValue } from 'react-hook-form';
import { Select, SelectItem, Switch, Textarea } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import type { EditAgentFormValues } from './voiceAgentEditFormTypes';

interface SttOption {
  value: string;
  label: string;
}

interface VoiceAgentSummarizationSectionProps {
  control: Control<EditAgentFormValues>;
  setValue: UseFormSetValue<EditAgentFormValues>;
  sttProviders: SttOption[];
  sttModelsForProvider: SttOption[];
}

export function VoiceAgentSummarizationSection({
  control,
  setValue,
  sttProviders,
  sttModelsForProvider,
}: Readonly<VoiceAgentSummarizationSectionProps>) {
  const enabled = useWatch({ control, name: 'summarization_enabled', defaultValue: false });
  const provider = useWatch({ control, name: 'summarization.provider', defaultValue: '' });

  return (
    <div className="rounded-xl border border-divider bg-content1">
      <div className="flex items-start justify-between gap-4 p-4">
        <div className="flex min-w-0 items-start gap-3">
          <div className="mt-0.5 rounded-lg bg-default-100 p-2 text-primary">
            <Icon icon="solar:document-text-linear" width={20} />
          </div>
          <div>
            <p className="font-medium text-foreground">Summarization</p>
            <p className="mt-0.5 text-sm text-default-500">
              Generate call summaries using a speech-to-text provider and model.
            </p>
          </div>
        </div>
        <Controller
          name="summarization_enabled"
          control={control}
          defaultValue={false}
          render={({ field }) => (
            <Switch
              aria-label="Enable summarization"
              isSelected={field.value ?? false}
              onValueChange={field.onChange}
            />
          )}
        />
      </div>

      {enabled && (
        <div className="space-y-4 border-t border-divider p-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Controller
              name="summarization.provider"
              control={control}
              render={({ field }) => (
                <Select
                  label="STT Provider"
                  placeholder="Select provider"
                  selectedKeys={field.value ? [field.value] : []}
                  onSelectionChange={(keys) => {
                    const k = Array.from(keys)[0] as string | undefined;
                    field.onChange(k ?? '');
                    setValue('summarization.model', '');
                  }}
                  variant="bordered"
                  aria-label="Summarization STT provider"
                >
                  {sttProviders.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </Select>
              )}
            />
            <Controller
              name="summarization.model"
              control={control}
              render={({ field }) => (
                <Select
                  label="STT Model"
                  placeholder="Select model"
                  selectedKeys={field.value ? [field.value] : []}
                  onSelectionChange={(keys) => {
                    const k = Array.from(keys)[0] as string | undefined;
                    field.onChange(k ?? '');
                  }}
                  variant="bordered"
                  isDisabled={!provider}
                  aria-label="Summarization STT model"
                >
                  {sttModelsForProvider.map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      {m.label}
                    </SelectItem>
                  ))}
                </Select>
              )}
            />
          </div>
          <Controller
            name="summarization.prompt_template"
            control={control}
            render={({ field }) => (
              <Textarea
                label="Prompt template"
                placeholder="Enter the summarization prompt template..."
                value={field.value ?? ''}
                onValueChange={field.onChange}
                minRows={4}
                variant="bordered"
                aria-label="Summarization prompt template"
              />
            )}
          />
        </div>
      )}
    </div>
  );
}
